/**
 * Общие элементы интерфейса.
 *
 * Перенесены из демо: там они выработались за несколько итераций правок
 * заказчика, и переизобретать их заново значило бы пройти тот же путь снова.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useBodyScrollLock } from './useBodyScrollLock.js';
import { useCloseOnBack } from './useCloseOnBack.js';
import { textOn } from '../theme.js';

// ---------------------------------------------------------------------------

/**
 * Подписанное поле формы.
 *
 * Это `<div>`, а НЕ `<label>`, и это осознанный отказ от стандартного
 * поведения: обычно щелчок по подписи переводит курсор в поле.
 *
 * Подпись обязана указывать на ОДИН элемент управления. Половина полей здесь
 * составные — раскрывающийся список это кнопка, строка поиска и несколько
 * десятков пунктов внутри, — и браузер связывает подпись с первым попавшимся
 * из них. Список раскрывался от щелчка по подписи, по подсказке под полем и
 * по пустому месту рядом, заметно за пределами самого окошка.
 *
 * Разделять поля на два вида — «простые с подписью-ярлыком» и «составные без
 * неё» — значило бы, что соседние поля одной формы ведут себя по-разному, и
 * это заметнее, чем потеря щелчка по подписи. Флажки и переключатели своей
 * подписью по-прежнему переключаются: там элемент управления ровно один.
 */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="block">
      <span className="mb-1 block text-xs font-medium text-ink2">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-ink2">{hint}</span>}
    </div>
  );
}

/**
 * Цветная плашка статуса.
 *
 * Заливка СПЛОШНАЯ, а не бледная подложка в десять процентов. Статус и оплата
 * — то, по чему журнал читают взглядом, не вчитываясь; полупрозрачная плашка
 * сливалась с чередованием строк и терялась среди текста.
 *
 * Текст ВСЕГДА белый — так решено для единообразия: плашка должна читаться
 * одинаково во всех строках, а не менять цвет надписи от статуса к статусу.
 */
export function Badge({
  children,
  color,
  muted = false,
}: {
  children: ReactNode;
  color: string;
  /**
   * Приглушённая плашка: бледная заливка и цветной текст вместо белого.
   *
   * Для состояний, которые НЕ ТРЕБУЮТ действия. Сплошная заливка кричит
   * одинаково громко и о беде, и о спокойном «письмо ещё не уходило»; в списке
   * из полусотни счетов от такого хора перестают выделяться те строки, ради
   * которых в список и заходят.
   */
  muted?: boolean;
}) {
  return (
    <span
      /*
        На телефоне плашка мельче. В таблице на широком экране она держит
        строку среди десятка колонок и должна быть заметной; в карточке
        телефона колонок нет, и та же плашка перекрикивает всё остальное —
        фамилию врача, конструкцию, сроки.
      */
      className="inline-flex max-w-full items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-semibold md:rounded-lg md:px-2.5 md:py-1 md:text-xs"
      style={
        muted
          ? {
              background: `color-mix(in srgb, ${color} 12%, transparent)`,
              border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
              color,
            }
          : { background: color, color: textOn(color) }
      }
    >
      {children}
    </span>
  );
}

export function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  right,
  spreadRight = false,
}: {
  /** Иконка раздела в тёмном квадрате — как в демо. */
  icon?: (props: { size?: number | string; className?: string }) => ReactNode;
  title: string;
  subtitle?: ReactNode;
  right?: ReactNode;
  /**
   * Растянуть блок кнопок на всю оставшуюся ширину (только широкий экран).
   *
   * Нужно там, где кнопки расходятся по краям ряда: главное действие слева,
   * сохранение справа. Без этого блок сжат по содержимому и прижат вправо
   * целиком — развести кнопки внутри него нечем.
   */
  spreadRight?: boolean;
}) {
  /*
   * На телефоне значок и название раздела СКРЫТЫ: и то и другое уже стоит
   * в верхней полосе, а вместе они съедали около полутора сантиметров экрана —
   * на списке заказов это два потерянных заказа. Подпись остаётся: в ней
   * живые сведения («зарплаты за август 2026»), которых больше нигде нет.
   *
   * На телефоне содержимое прижато ВЛЕВО, кнопки уходят вправо только на
   * широком экране. При `justify-between` их распирало по краям строки, а на
   * экранах, где левая часть шапки скрыта целиком (в «Заказах» там только
   * значок, название и подпись — всё настольное), они улетали к правому краю,
   * оставляя слева пустоту в пол-экрана.
   */
  return (
    <div className="mb-2.5 flex flex-wrap items-start gap-2 md:mb-4 md:gap-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <span
            className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-white md:flex"
            style={{ background: 'var(--otc-accent-dark)' }}
          >
            <Icon size={28} />
          </span>
        )}
        <div>
          <h1 className="hidden font-display text-xl font-semibold leading-tight text-ink md:block">{title}</h1>
          {subtitle && <p className="text-xs text-ink2 md:text-sm">{subtitle}</p>}
        </div>
      </div>
      {/*
        `flex-shrink-0` здесь БЫЛО ошибкой: блок кнопок сохранял свою полную
        ширину и на телефоне выпирал за правый край экрана вместе со всей
        страницей. Без него блок сжимается, и его собственный перенос строк
        наконец срабатывает — кнопки встают друг под другом.
      */}
      {right && (
        <div
          className={
            'flex min-w-0 flex-wrap items-center gap-2 md:ml-auto' + (spreadRight ? ' md:flex-1' : '')
          }
        >
          {right}
        </div>
      )}
    </div>
  );
}

/** Кнопка в плавающей панели действий над выделенными заказами. */
export function SelectionActionButton({
  icon: Icon,
  label,
  color,
  onClick,
  disabled,
  title,
}: {
  icon: (props: { size?: number | string }) => ReactNode;
  label: string;
  color: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      // Тот же отклик, что у пунктов меню: подсветка, значок чуть подрастает,
      // при нажатии кнопка вдавливается. Это вторая по частоте группа
      // «значок + подпись» в программе, и вести себя она должна так же.
      // На широком экране крупные: подпись в девять пикселей читалась только
      // при хорошем свете. На телефоне теснее — там панель и так занимает
      // треть экрана, и каждый лишний пиксель закрывает строку заказа.
      className="otc-action flex flex-shrink-0 flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-40 md:gap-1 md:px-4 md:py-2"
      style={{ color }}
    >
      {/* Размер значка задаётся стилями: одно число не годится сразу
          для тесного телефона и просторного широкого экрана. */}
      <span className="otc-nav-icon [&_svg]:h-4 [&_svg]:w-4 md:[&_svg]:h-[19px] md:[&_svg]:w-[19px]">
        <Icon size={19} />
      </span>
      <span className="text-[10px] font-medium leading-none md:text-[11px]">{label}</span>
    </button>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  title,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  title?: string;
  type?: 'button' | 'submit';
}) {
  /*
    На телефоне кнопка мельче.

    В полную величину «Создать заказ» и «Сводный отчёт» не помещались в одну
    строку на узких телефонах и вставали друг под другом — две строки шапки
    там, где нужна одна, а каждая строка шапки съедает заказ из списка.
  */
  const base =
    /*
      Рамка есть у ВСЕХ видов, у сплошных — прозрачная.

      Без неё тёмная кнопка выходила на две точки ниже белой, стоящей рядом
      с той же начинкой: у белой рамка занимает по точке сверху и снизу.
    */
    'inline-flex items-center justify-center gap-1.5 rounded-lg border border-transparent px-2.5 py-1.5 text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed md:gap-1.5 md:px-3.5 md:py-2 md:text-sm';

  const styles: Record<string, string> = {
    primary: 'text-white',
    secondary: 'border text-ink2 hover:bg-black/5',
    danger: 'text-white',
    ghost: 'text-ink2 hover:bg-black/5',
  };

  const inline: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--otc-accent)' },
    secondary: { borderColor: 'var(--otc-line)' },
    danger: { background: 'var(--otc-coral)' },
    ghost: {},
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${base} ${styles[variant]}`}
      style={inline[variant]}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------

/**
 * Модальное окно.
 *
 * Закрывается по Escape и по клику мимо. Обе привычки настолько въелись, что
 * окно без них воспринимается как зависшее.
 */
export function Modal({
  title,
  children,
  onClose,
  size = 'md',
  footer,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Страница позади не должна уезжать, пока окно открыто.
  useBodyScrollLock(true);

  // «Назад» на телефоне закрывает окно, а не выходит из программы.
  useCloseOnBack(true, onClose);

  const widths = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    // Для карточек с несколькими юрлицами: они встают в две колонки,
    // и окно расширяется вместо того, чтобы вытягиваться в высоту.
    '2xl': 'max-w-7xl',
  };

  return (
    /*
     * Клик по затемнению НЕ закрывает окно.
     *
     * В карточке клиники два десятка полей: промах мимо поля или выделение
     * текста с выходом за край окна закрывали его вместе с введённым.
     * Закрыть можно крестиком, кнопкой «Закрыть» или Escape — всеми тремя
     * способами намеренно, а не случайным движением.
     */
    <div
      /*
        На ТЕЛЕФОНЕ поля вокруг окна урезаны, а само окно выше.

        Прежние отступы в шестнадцать точек с каждой стороны съедали
        восьмую часть ширины экрана — а внутри таблица прайса, где каждая
        колонка на счету, и править её приходилось, листая окно вбок.
        На широком экране поля остаются: там место есть, и окно во весь
        экран выглядело бы неопрятно.
      */
      className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4"
      style={{ background: 'rgba(28,27,24,0.5)' }}
    >
      <div
        className={`flex max-h-[96vh] w-full ${widths[size]} flex-col rounded-xl bg-bg shadow-xl md:max-h-[92vh]`}
      >
        <div className="flex flex-shrink-0 items-center justify-between px-3 pb-2 pt-4 md:px-5 md:pb-3 md:pt-5">
          <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-black/5" aria-label="Закрыть">
            <X size={18} className="text-ink2" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3 md:px-5 md:pb-5">{children}</div>
        {/*
          Подвал — ОТДЕЛЬНАЯ полоса, а не кнопки, брошенные под содержимым.

          Раньше он не отличался от тела окна ничем: ни линии, ни заливки, ни
          отступа снизу у содержимого. Кнопка висела в углу вплотную к краю и
          читалась как забытая, а не как завершение окна. Теперь полоса
          отделена линией и своей заливкой — как шапка сверху.

          Подвал ПЕРЕНОСИТСЯ по строкам. Кнопки стояли в один ряд без переноса,
          и на телефоне последняя уезжала за край окна: «Отправить на почту»
          в счёте было не нажать, а предупреждение о несохранённых правках
          обрывалось на полуслове.
        */}
        {footer && (
          <div
            className="flex flex-shrink-0 flex-wrap items-center gap-2 rounded-b-xl border-t px-5 py-3.5"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Подтверждение опасного действия прямо на месте, без модального окна.
 *
 * Перенесено из демо: там удаление подтверждалось раскрывающейся полоской
 * рядом с кнопкой. Это заметно быстрее модального окна при работе со списком,
 * где удалять приходится по нескольку записей подряд.
 */
export function ConfirmInline({
  question,
  confirmLabel = 'Да, удалить',
  onConfirm,
  onCancel,
}: {
  question: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-3 py-2"
      style={{ borderColor: 'var(--otc-coral)', background: 'rgba(192,69,60,0.05)' }}
    >
      <span className="flex-1 text-xs" style={{ color: 'var(--otc-coral)' }}>
        {question}
      </span>
      <button
        onClick={onConfirm}
        className="rounded-md px-2.5 py-1 text-xs text-white"
        style={{ background: 'var(--otc-coral)' }}
      >
        {confirmLabel}
      </button>
      <button
        onClick={onCancel}
        className="rounded-md border px-2.5 py-1 text-xs text-ink2"
        style={{ borderColor: 'var(--otc-line)' }}
      >
        Отмена
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface ToastState {
  text: string;
  tone: 'info' | 'error';
}

/** Всплывающее уведомление. Держится 3,5 с — хватает прочитать длинную фразу об отказе. */
export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = (text: string, tone: ToastState['tone'] = 'info') => {
    window.clearTimeout(timer.current);
    setToast({ text, tone });
    timer.current = window.setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { toast, show };
}

export function Toast({ toast }: { toast: ToastState | null }) {
  if (!toast) return null;
  return (
    <div
      className="fixed bottom-6 left-1/2 z-[100] max-w-xl -translate-x-1/2 rounded-lg px-4 py-2.5 text-sm text-white shadow-lg"
      style={{ background: toast.tone === 'error' ? 'var(--otc-coral)' : 'var(--otc-ink)' }}
      role="status"
    >
      {toast.text}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function EmptyState({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm text-ink2">{text}</p>
      {action}
    </div>
  );
}

export function Spinner({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-ink2">
      <span
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        aria-hidden
      />
      {label}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm" style={{ color: 'var(--otc-coral)' }}>
        {error.message}
      </p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  );
}
